import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { ApiService, IPagingResponse, PagingApiService } from '@core/http';
import { environment } from '@env';
import { HttpClient, HttpResponse } from '@angular/common/http';
import { IPatientDocument } from '@project/view-models';
import { TranslateService } from '@project/translate';
import { NotificationsService } from '@lib/notifications';
import { map } from 'rxjs/operators';

export enum AttachmentType {
  MealPlan = 1,
}

@Injectable({
  providedIn: 'root',
})
export class PlansService {
  constructor(
    private readonly apiService: ApiService,
    private http: HttpClient,
    private pagingApiService: PagingApiService,
    private notificationsService: NotificationsService,
  ) {}

  uploadFile(requisitionId: string, type: AttachmentType, file: File): Observable<any> {
    const isPdf = file.type === 'application/pdf' || file.name.toLowerCase().endsWith('.pdf');
    if (!isPdf) {
      this.notificationsService.error({
        message: TranslateService.localize('errors.upload.files'),
      });
      return new Observable();
    }

    const url = `${environment.environmentVariables.apiCoreUrl}/Attachment/${requisitionId}/${type}`;
    const formData = new FormData();
    formData.append('file', file);

    return this.apiService.postWithForm(url, formData);
  }

  downloadFile(attachmentId: string): Observable<string> {
    const url = `${environment.environmentVariables.apiCoreUrl}/Attachment/${attachmentId}/download`;

    return this.http.get(url, {
      withCredentials: true,
      responseType: 'text',
    }) as Observable<string>;
  }

  private httpToPagingResponse(resp: HttpResponse<string>, itemsPerPage: number): IPagingResponse<IPatientDocument> {
    const total = Number(resp.headers.get('PTM-Total-Count') ?? 0);

    const raw = resp.body
      ? (() => {
          try {
            return JSON.parse(resp.body);
          } catch {
            return null;
          }
        })()
      : null;
    const items: IPatientDocument[] = Array.isArray(raw)
      ? (raw as IPatientDocument[])
      : Array.isArray((raw as any)?.items)
      ? ((raw as any).items as IPatientDocument[])
      : [];

    return {
      items,
      totalItemsCount: total,
      requestTotalItemsCount: total,
      itemsPerPage,
      requestTotalPages: itemsPerPage > 0 ? Math.ceil(total / itemsPerPage) : 0,
    };
  }

  getAttachmentsByUserId(
    userId: string,
    page: number,
    itemsPerPage: number,
  ): Observable<IPagingResponse<IPatientDocument>> {
    const offset = (page - 1) * itemsPerPage;
    const url = `${environment.environmentVariables.apiCoreUrl}/Attachment/user/${userId}`;

    return this.http
      .get(url, {
        observe: 'response',
        responseType: 'text',
        withCredentials: true,
        params: { limit: itemsPerPage, offset } as any,
      })
      .pipe(map((resp) => this.httpToPagingResponse(resp, itemsPerPage)));
  }

  getAttachmentsByRequisitionId(
    requisitionId: string,
    page: number,
    itemsPerPage: number,
  ): Observable<IPagingResponse<IPatientDocument>> {
    const offset = (page - 1) * itemsPerPage;
    const url = `${environment.environmentVariables.apiCoreUrl}/Attachment/requisition/${requisitionId}`;
    const params = new Map<string, any>([
      ['limit', itemsPerPage],
      ['offset', offset],
    ]);

    return this.pagingApiService.request<any, IPatientDocument>({
      url,
      additionalQueryParams: params,
      pageNumber: page,
      itemsPerPage,
      itemFactory: (dto) => ({
        ...dto,
        createdAt: new Date(dto.uploaded_date),
        documentType: dto.type,
      }),
    });
  }
}
